"use client";

import { c as _c } from "react/compiler-runtime";
import { IconMagnifyingGlass } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconCheckOutline18, IconChevronLeftOutline18, IconChevronRightOutline18 } from "nucleo-ui-outline-18";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTagCategories } from "@/hooks/useTagCategories";
import type { TagScope } from "@/hooks/useTags";
import { useTags } from "@/hooks/useTags";
import type { Tag } from "@/stores/templatesStore";
import type { WheelEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
const SCROLL_FADE_THRESHOLD = 2;
export interface TagSelectorProps {
  selectedTags: string[];
  onSelectionChange?: (tagSlugs: string[]) => void;
  /** Called with full Tag objects when selection changes. Use this to access tag IDs. */
  onTagsChange?: (tags: Tag[]) => void;
  label?: string;
  searchPlaceholder?: string;
  tagsPerPage?: number;
  showSearch?: boolean;
  showPagination?: boolean;
  showCategoryFilter?: boolean;
  showSelectionCount?: boolean;
  showImages?: boolean;
  showCount?: boolean;
  sortByCount?: boolean;
  scope?: TagScope;
  compact?: boolean;
  multiSelect?: boolean;
  minSelection?: number;
  maxSelection?: number;
  className?: string;
  gridColumns?: number;
  variant?: "grid" | "flow";
  /** Client-side filter applied after API fetch. Useful to restrict to category-style tags (e.g. `t => !t.imageUrl`). */
  filter?: (tag: Tag) => boolean;
  /** Seed the internal tag-object map so pre-selected tags survive even if they're on a different page. */
  initialTagObjects?: Tag[];
}
export function TagSelector(t0) {
  const $ = _c(75);
  const {
    selectedTags,
    onSelectionChange,
    onTagsChange,
    label: t1,
    searchPlaceholder: t2,
    tagsPerPage: t3,
    showSearch: t4,
    showPagination: t5,
    showCategoryFilter: t6,
    showSelectionCount: t7,
    showImages: t8,
    showCount: t9,
    sortByCount: t10,
    scope: t11,
    compact: t12,
    multiSelect: t13,
    minSelection: t14,
    maxSelection,
    className: t15,
    gridColumns,
    variant: t16,
    filter,
    initialTagObjects
  } = t0;
  const label = t1 === undefined ? "Tags" : t1;
  const searchPlaceholder = t2 === undefined ? "Search apps..." : t2;
  const tagsPerPage = t3 === undefined ? 15 : t3;
  const showSearch = t4 === undefined ? true : t4;
  const showPagination = t5 === undefined ? true : t5;
  const showCategoryFilter = t6 === undefined ? true : t6;
  t7 === undefined ? true : t7;
  const showImages = t8 === undefined ? true : t8;
  const showCount = t9 === undefined ? false : t9;
  const sortByCount = t10 === undefined ? false : t10;
  const scope = t11 === undefined ? "all" : t11;
  const compact = t12 === undefined ? false : t12;
  const multiSelect = t13 === undefined ? true : t13;
  const minSelection = t14 === undefined ? 0 : t14;
  const className = t15 === undefined ? "" : t15;
  const variant = t16 === undefined ? "grid" : t16;
  const [tagSearch, setTagSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(null);
  let t17;
  if ($[0] !== initialTagObjects) {
    t17 = () => {
      const map = new Map();
      if (initialTagObjects) {
        for (const tag of initialTagObjects) {
          map.set(tag.slug, tag);
        }
      }
      return map;
    };
    $[0] = initialTagObjects;
    $[1] = t17;
  } else {
    t17 = $[1];
  }
  const [selectedTagObjects, setSelectedTagObjects] = useState(t17);
  let t18;
  let t19;
  if ($[2] !== tagSearch) {
    t18 = () => {
      const timer = setTimeout(() => {
        setDebouncedSearch(tagSearch);
      }, 300);
      return () => clearTimeout(timer);
    };
    t19 = [tagSearch];
    $[2] = tagSearch;
    $[3] = t18;
    $[4] = t19;
  } else {
    t18 = $[3];
    t19 = $[4];
  }
  useEffect(t18, t19);
  const cols = gridColumns || (compact ? 4 : 3);
  const effectiveTagsPerPage = variant === "grid" ? Math.ceil(tagsPerPage / cols) * cols : tagsPerPage;
  const offset = currentPage * effectiveTagsPerPage;
  const t20 = selectedCategory || undefined;
  const t21 = debouncedSearch || undefined;
  let t22;
  if ($[5] !== effectiveTagsPerPage || $[6] !== offset || $[7] !== scope || $[8] !== sortByCount || $[9] !== t20 || $[10] !== t21) {
    t22 = {
      category: t20,
      search: t21,
      limit: effectiveTagsPerPage,
      offset,
      sortByCount,
      scope
    };
    $[5] = effectiveTagsPerPage;
    $[6] = offset;
    $[7] = scope;
    $[8] = sortByCount;
    $[9] = t20;
    $[10] = t21;
    $[11] = t22;
  } else {
    t22 = $[11];
  }
  const {
    tags,
    hasMore: hasNextPage,
    loading: tagsLoading
  } = useTags(t22);
  let t23;
  let t24;
  if ($[12] !== onTagsChange || $[13] !== selectedTags || $[14] !== tags) {
    t23 = () => {
      if (!onTagsChange) {
        return;
      }
      setSelectedTagObjects(prev => {
        const next = new Map(prev);
        let changed = false;
        for (const tag_0 of tags) {
          if (selectedTags.includes(tag_0.slug) && !next.has(tag_0.slug)) {
            next.set(tag_0.slug, tag_0);
            changed = true;
          }
        }
        for (const slug of prev.keys()) {
          if (!selectedTags.includes(slug)) {
            next.delete(slug);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    };
    t24 = [tags, selectedTags, onTagsChange];
    $[12] = onTagsChange;
    $[13] = selectedTags;
    $[14] = tags;
    $[15] = t23;
    $[16] = t24;
  } else {
    t23 = $[15];
    t24 = $[16];
  }
  useEffect(t23, t24);
  let t25;
  let result = tags;
  if (showCount) {
    let t26;
    if ($[17] !== result) {
      t26 = result.filter(_temp);
      $[17] = result;
      $[18] = t26;
    } else {
      t26 = $[18];
    }
    result = t26;
  }
  if (filter) {
    let t26;
    if ($[19] !== filter || $[20] !== result) {
      t26 = result.filter(filter);
      $[19] = filter;
      $[20] = result;
      $[21] = t26;
    } else {
      t26 = $[21];
    }
    result = t26;
  }
  t25 = result;
  const visibleTags = t25;
  const {
    categories: fetchedCategories
  } = useTagCategories();
  let t26;
  if ($[22] !== fetchedCategories || $[23] !== showCategoryFilter) {
    t26 = showCategoryFilter ? fetchedCategories : [];
    $[22] = fetchedCategories;
    $[23] = showCategoryFilter;
    $[24] = t26;
  } else {
    t26 = $[24];
  }
  const categories = t26;
  const hasPrevPage = currentPage > 0;
  const filterKey = `${debouncedSearch}-${selectedCategory}`;
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey);
  if (filterKey !== prevFilterKey) {
    setPrevFilterKey(filterKey);
    setCurrentPage(0);
  }
  let t27;
  if ($[25] !== maxSelection || $[26] !== multiSelect || $[27] !== onSelectionChange || $[28] !== onTagsChange || $[29] !== selectedTagObjects || $[30] !== selectedTags || $[31] !== tags) {
    t27 = tagSlug => {
      const tagObject = tags.find(t => t.slug === tagSlug);
      if (!multiSelect) {
        onSelectionChange?.([tagSlug]);
        if (onTagsChange && tagObject) {
          const newMap = new Map();
          newMap.set(tagSlug, tagObject);
          setSelectedTagObjects(newMap);
          onTagsChange([tagObject]);
        }
        return;
      }
      const isSelected = selectedTags.includes(tagSlug);
      let newSlugs;
      let newTagObjects;
      if (isSelected) {
        newSlugs = selectedTags.filter(slug_0 => slug_0 !== tagSlug);
        newTagObjects = new Map(selectedTagObjects);
        newTagObjects.delete(tagSlug);
      } else {
        if (maxSelection && selectedTags.length >= maxSelection) {
          return;
        }
        newSlugs = [...selectedTags, tagSlug];
        newTagObjects = new Map(selectedTagObjects);
        if (tagObject) {
          newTagObjects.set(tagSlug, tagObject);
        }
      }
      onSelectionChange?.(newSlugs);
      setSelectedTagObjects(newTagObjects);
      if (onTagsChange) {
        onTagsChange(Array.from(newTagObjects.values()));
      }
    };
    $[25] = maxSelection;
    $[26] = multiSelect;
    $[27] = onSelectionChange;
    $[28] = onTagsChange;
    $[29] = selectedTagObjects;
    $[30] = selectedTags;
    $[31] = tags;
    $[32] = t27;
  } else {
    t27 = $[32];
  }
  const toggleTag = t27;
  const categoryScrollRef = useRef(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);
  let t28;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = () => {
      const viewport = categoryScrollRef.current?.querySelector("[data-slot=\"scroll-area-viewport\"]");
      if (!viewport) {
        return;
      }
      setShowLeftFade(viewport.scrollLeft > SCROLL_FADE_THRESHOLD);
      setShowRightFade(viewport.scrollLeft + viewport.clientWidth < viewport.scrollWidth - SCROLL_FADE_THRESHOLD);
    };
    $[33] = t28;
  } else {
    t28 = $[33];
  }
  const updateScrollFades = t28;
  let t29;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = () => {
      const viewport_0 = categoryScrollRef.current?.querySelector("[data-slot=\"scroll-area-viewport\"]");
      if (!viewport_0) {
        return;
      }
      updateScrollFades();
      viewport_0.addEventListener("scroll", updateScrollFades, {
        passive: true
      });
      window.addEventListener("resize", updateScrollFades);
      return () => {
        viewport_0.removeEventListener("scroll", updateScrollFades);
        window.removeEventListener("resize", updateScrollFades);
      };
    };
    $[34] = t29;
  } else {
    t29 = $[34];
  }
  let t30;
  if ($[35] !== categories) {
    t30 = [categories, updateScrollFades];
    $[35] = categories;
    $[36] = t30;
  } else {
    t30 = $[36];
  }
  useEffect(t29, t30);
  const handleCategoryWheel = _temp2;
  const meetsMinimum = !minSelection || selectedTags.length >= minSelection;
  const t31 = `min-w-0 max-w-full space-y-2 overflow-x-hidden ${className}`;
  let t32;
  if ($[37] !== label || $[38] !== meetsMinimum || $[39] !== minSelection) {
    t32 = label && <div className="flex items-center justify-between"><Label>{label}</Label>{minSelection && minSelection > 0 && !meetsMinimum && <span className="text-xs text-amber-600 dark:text-amber-400">Select at least {minSelection} tag{minSelection > 1 ? "s" : ""}</span>}</div>;
    $[37] = label;
    $[38] = meetsMinimum;
    $[39] = minSelection;
    $[40] = t32;
  } else {
    t32 = $[40];
  }
  let t33;
  if ($[41] !== compact || $[42] !== searchPlaceholder || $[43] !== showSearch || $[44] !== tagSearch) {
    t33 = showSearch && <div className={`relative ${compact ? "mb-3" : "mb-4"}`}><IconMagnifyingGlass className={`text-ds-text-disabled absolute left-3 top-1/2 -translate-y-1/2 ${compact ? "h-3 w-3" : "h-4 w-4"}`} /><Input type="text" placeholder={searchPlaceholder} value={tagSearch} onChange={e => setTagSearch(e.target.value)} className={`pl-9 ${compact ? "text-xs" : ""}`} /></div>;
    $[41] = compact;
    $[42] = searchPlaceholder;
    $[43] = showSearch;
    $[44] = tagSearch;
    $[45] = t33;
  } else {
    t33 = $[45];
  }
  let t34;
  if ($[46] !== categories || $[47] !== compact || $[48] !== selectedCategory || $[49] !== showCategoryFilter || $[50] !== showLeftFade || $[51] !== showRightFade) {
    t34 = showCategoryFilter && categories.length > 0 && <div ref={categoryScrollRef} className={`relative ${compact ? "mb-2" : "mb-3"} w-full min-w-0 max-w-full overflow-hidden`} onWheel={handleCategoryWheel}>{showLeftFade && <div className="dark:from-ds-neutral-900 pointer-events-none absolute left-0 top-0 z-10 h-full w-6 bg-gradient-to-r from-white" />}<ScrollArea className="w-full max-w-full" scrollbarOrientation="horizontal"><div className="flex w-max gap-2 pb-2"><button type="button" onClick={() => setSelectedCategory(null)} className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-all ${selectedCategory === null ? "bg-blue-500 text-white" : "bg-ds-neutral-100 text-ds-text-secondary hover:bg-ds-neutral-200 dark:bg-ds-neutral-800 dark:hover:bg-ds-neutral-700"} ${compact ? "text-3xs px-2 py-0.5" : ""}`}>All</button>{categories.map(category => <button key={category} type="button" onClick={() => setSelectedCategory(category)} className={`shrink-0 whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium capitalize transition-all ${selectedCategory === category ? "bg-blue-500 text-white" : "bg-ds-neutral-100 text-ds-text-secondary hover:bg-ds-neutral-200 dark:bg-ds-neutral-800 dark:hover:bg-ds-neutral-700"} ${compact ? "text-3xs px-2 py-0.5" : ""}`}>{category}</button>)}</div></ScrollArea>{showRightFade && <div className="dark:from-ds-neutral-900 pointer-events-none absolute right-0 top-0 z-10 h-full w-6 bg-gradient-to-l from-white" />}</div>;
    $[46] = categories;
    $[47] = compact;
    $[48] = selectedCategory;
    $[49] = showCategoryFilter;
    $[50] = showLeftFade;
    $[51] = showRightFade;
    $[52] = t34;
  } else {
    t34 = $[52];
  }
  let t35;
  if ($[53] !== cols || $[54] !== compact || $[55] !== debouncedSearch || $[56] !== hasNextPage || $[57] !== hasPrevPage || $[58] !== maxSelection || $[59] !== selectedCategory || $[60] !== selectedTags || $[61] !== showCount || $[62] !== showImages || $[63] !== showPagination || $[64] !== tagsLoading || $[65] !== toggleTag || $[66] !== variant || $[67] !== visibleTags) {
    t35 = tagsLoading ? <div className={`${compact ? "text-xs" : "text-sm"} text-ds-text-muted`}>{debouncedSearch ? "Searching..." : "Loading tags..."}</div> : <>{visibleTags.length === 0 ? <div className={`${compact ? "text-xs" : "text-sm"} text-ds-text-muted`}>{debouncedSearch || selectedCategory ? "No tags match your filters" : "No tags available"}</div> : <div className={variant === "flow" ? `flex flex-wrap gap-2 ${compact ? "gap-1.5" : ""}` : `grid w-full min-w-0 max-w-full gap-2 ${compact ? "min-h-[80px] gap-1.5" : "min-h-[120px]"}`} style={variant === "grid" ? {
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`
      } : undefined}>{visibleTags.map(tag_2 => {
          const isSelected_0 = selectedTags.includes(tag_2.slug);
          const isDisabled = !isSelected_0 && maxSelection !== undefined && selectedTags.length >= maxSelection;
          const hasImage = showImages && tag_2.imageUrl;
          let tooltipMessage = tag_2.description || tag_2.name;
          if (isDisabled) {
            tooltipMessage = `${tag_2.name} (Maximum ${maxSelection} tags reached)`;
          }
          return <button key={tag_2.id} type="button" onClick={() => toggleTag(tag_2.slug)} disabled={isDisabled} className={`flex items-center gap-1.5 py-2 ${variant === "flow" ? "gap-2" : "w-full min-w-0 justify-center"} overflow-hidden rounded-lg border font-medium transition-all ${isSelected_0 ? "shadow-ds-soft border-blue-600 bg-blue-600 text-white" : isDisabled ? "border-ds-border-default bg-ds-neutral-100 text-ds-text-disabled dark:bg-ds-neutral-800 cursor-not-allowed" : "border-ds-neutral-200 text-ds-text-secondary bg-ds-surface-card transition-all hover:-translate-y-0.5 hover:border-blue-400 hover:bg-blue-50 hover:shadow-[0_2px_8px_0_rgba(37,99,235,0.08)] dark:border-zinc-600 dark:text-zinc-100 dark:hover:border-blue-400 dark:hover:bg-blue-900/30 dark:hover:shadow-[0_2px_8px_0_rgba(37,99,235,0.16)]"} ${compact ? "px-2 py-1 text-xs" : variant === "flow" ? "px-4 py-2 text-sm font-semibold" : "px-3 py-2 text-sm"}`} title={tooltipMessage}>{hasImage && <img src={tag_2.imageUrl} alt={tag_2.name} className={`${compact ? "h-3 w-3" : "h-4 w-4"} flex-shrink-0 rounded object-cover`} onError={_temp3} />}{isSelected_0 && variant === "grid" && <IconCheckOutline18 className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} />}<span className={variant === "grid" ? "truncate" : ""}>{tag_2.name}</span>{showCount && tag_2.count !== undefined && <span className={`rounded-full px-1.5 py-0.5 text-xs font-medium ${isSelected_0 ? "bg-blue-400 text-white" : "bg-ds-neutral-300 text-ds-text-secondary dark:bg-zinc-600"}`}>{tag_2.count}</span>}</button>;
        })}</div>}{showPagination && (hasPrevPage || hasNextPage) && <div className={`flex items-center justify-center ${compact ? "pt-2" : "pt-3"}`}><div className={`flex ${compact ? "gap-1" : "gap-2"}`}><button type="button" onClick={() => setCurrentPage(_temp4)} disabled={!hasPrevPage} className={`bg-ds-neutral-200 hover:bg-ds-neutral-300 dark:bg-ds-neutral-700 flex items-center gap-1 rounded-md text-zinc-800 disabled:cursor-not-allowed disabled:opacity-50 dark:text-zinc-100 dark:hover:bg-zinc-600 ${compact ? "px-2 py-1 text-xs" : "px-3 py-2 text-sm"}`}><IconChevronLeftOutline18 className={compact ? "h-3 w-3" : "h-4 w-4"} />{!compact && "Previous"}</button><button type="button" onClick={() => setCurrentPage(_temp5)} disabled={!hasNextPage || visibleTags.length === 0} className={`bg-ds-neutral-200 hover:bg-ds-neutral-300 dark:bg-ds-neutral-700 flex items-center gap-1 rounded-md text-zinc-800 disabled:cursor-not-allowed disabled:opacity-50 dark:text-zinc-100 dark:hover:bg-zinc-600 ${compact ? "px-2 py-1 text-xs" : "px-3 py-2 text-sm"}`}>{!compact && "Next"}<IconChevronRightOutline18 className={compact ? "h-3 w-3" : "h-4 w-4"} /></button></div></div>}</>;
    $[53] = cols;
    $[54] = compact;
    $[55] = debouncedSearch;
    $[56] = hasNextPage;
    $[57] = hasPrevPage;
    $[58] = maxSelection;
    $[59] = selectedCategory;
    $[60] = selectedTags;
    $[61] = showCount;
    $[62] = showImages;
    $[63] = showPagination;
    $[64] = tagsLoading;
    $[65] = toggleTag;
    $[66] = variant;
    $[67] = visibleTags;
    $[68] = t35;
  } else {
    t35 = $[68];
  }
  let t36;
  if ($[69] !== t31 || $[70] !== t32 || $[71] !== t33 || $[72] !== t34 || $[73] !== t35) {
    t36 = <div className={t31}>{t32}{t33}{t34}{t35}</div>;
    $[69] = t31;
    $[70] = t32;
    $[71] = t33;
    $[72] = t34;
    $[73] = t35;
    $[74] = t36;
  } else {
    t36 = $[74];
  }
  return t36;
}
function _temp5(prev_1) {
  return prev_1 + 1;
}
function _temp4(prev_0) {
  return prev_0 - 1;
}
function _temp3(e_0) {
  e_0.currentTarget.style.display = "none";
}
function _temp2(event) {
  const viewport_1 = event.currentTarget.querySelector("[data-slot=\"scroll-area-viewport\"]");
  if (!viewport_1) {
    return;
  }
  if (event.deltaX === 0 && event.deltaY !== 0) {
    viewport_1.scrollLeft = viewport_1.scrollLeft + event.deltaY;
    event.preventDefault();
  }
}
function _temp(tag_1) {
  return tag_1.count && tag_1.count > 0;
}